<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12" md="2"></v-col>
      <v-col cols="12" sm="12" md="8">
        <v-card class="mx-auto my-5 mb-16" max-width="1000">
          <v-app-bar flat>
            <span class="headline">{{ isNew ? $t("asaMeeting.new") : $t("asaMeeting.edit") }}</span>
          </v-app-bar>
          <v-card-text>
            <v-container>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="auto"
                    :disabled="isReadOnlyUser"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDate(form.date)"
                        :label="$t('asaMeeting.date') + ' *'"
                        prepend-icon="mdi-calendar"
                        readonly
                        :disabled="isReadOnlyUser"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker v-model="form.date" no-title :locale="$i18n.locale" @input="dateMenu = false" />
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="8">
                  <v-text-field v-model="form.title" :label="$t('asaMeeting.title')" :disabled="isReadOnlyUser" />
                </v-col>
              </v-row>

              <v-combobox
                v-model="participants"
                :items="users"
                item-text="name"
                item-value="_id"
                :label="$t('asaMeeting.participants')"
                :hint="$t('asaMeeting.participantsHint')"
                persistent-hint
                multiple
                chips
                small-chips
                deletable-chips
                prepend-icon="mdi-account-multiple-outline"
                :disabled="isReadOnlyUser"
              />

              <v-divider class="my-6" />
              <div class="subtitle-1 mb-3">{{ $t("asaMeeting.topics") }}</div>

              <div v-if="!form.topics.length" class="text-body-2 text--secondary mb-3">
                {{ $t("asaMeeting.noTopics") }}
              </div>

              <v-card v-for="(topic, idx) in form.topics" :key="idx" outlined class="mb-3 pa-3">
                <div class="d-flex align-center">
                  <span class="asa-topic__number">{{ idx + 1 }}.</span>
                  <v-text-field
                    v-model="topic.title"
                    :label="$t('asaMeeting.topicTitle')"
                    dense
                    hide-details
                    class="flex-grow-1"
                    :disabled="isReadOnlyUser"
                  />
                  <v-icon v-if="!isReadOnlyUser" class="ml-3" @click="form.topics.splice(idx, 1)">delete</v-icon>
                </div>
                <v-textarea
                  v-model="topic.comment"
                  :label="$t('asaMeeting.comment')"
                  outlined
                  dense
                  hide-details
                  auto-grow
                  rows="2"
                  class="mt-3"
                  :disabled="isReadOnlyUser"
                />
                <v-row dense align="end" class="mt-2">
                  <v-col cols="12">
                    <v-combobox
                      :value="responsibleItems(topic)"
                      :items="users"
                      item-text="name"
                      item-value="_id"
                      :label="$t('asaMeeting.responsibles')"
                      prepend-icon="mdi-account-outline"
                      multiple
                      small-chips
                      deletable-chips
                      dense
                      hide-details
                      :disabled="isReadOnlyUser"
                      @change="setResponsibles(topic, $event)"
                    />
                  </v-col>
                  <v-col cols="6" md>
                    <v-menu
                      v-model="topic.dueMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="auto"
                      :disabled="isReadOnlyUser"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDate(topic.dueDate)"
                          :label="$t('asaMeeting.dueDate')"
                          prepend-icon="event"
                          class="asa-topic__date"
                          readonly
                          clearable
                          dense
                          hide-details
                          :disabled="isReadOnlyUser"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="topic.dueDate = null"
                        />
                      </template>
                      <v-date-picker
                        v-model="topic.dueDate"
                        no-title
                        :locale="$i18n.locale"
                        @input="topic.dueMenu = false"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="6" md>
                    <v-select
                      v-model="topic.priority"
                      :items="priorityOptions"
                      :label="$t('asaMeeting.priority')"
                      dense
                      hide-details
                      :disabled="isReadOnlyUser"
                    />
                  </v-col>
                  <v-col v-if="canCreateTickets || !!topic.ticket" cols="12" md="auto">
                  <v-btn
                    color="primary"
                    outlined
                    :block="$vuetify.breakpoint.smAndDown"
                    height="34"
                    :loading="creatingTicket === idx"
                    :disabled="!topic.title.trim() || isReadOnlyUser || (!!topic.ticket && (!needsSync(topic) || !canCreateTickets))"
                    @click="topic.ticket ? syncTicket(topic, idx) : createTicket(topic, idx)"
                  >
                    <span v-if="!topic.ticket">
                      {{ $t("asaMeeting.createTicket") }}
                      <v-icon class="ml-1" small>launch</v-icon>
                    </span>
                    <span v-else-if="needsSync(topic)">
                      {{ $t("asaMeeting.updateTicket") }}
                      <v-icon class="ml-1" small>sync</v-icon>
                    </span>
                    <span v-else-if="topic.ticketSource === 'picked'">
                      {{ $t("asaMeeting.ticketLinked") }}
                      <v-icon class="ml-1" small>check</v-icon>
                    </span>
                    <span v-else>
                      {{ $t("asaMeeting.ticketCreated") }}
                      <v-icon class="ml-1" small>check</v-icon>
                    </span>
                  </v-btn>
                  </v-col>
                </v-row>
              </v-card>

              <div v-if="!isReadOnlyUser" class="d-flex flex-wrap align-center">
                <v-btn text color="primary" small class="mr-2" @click="addOwnTopic">
                  <v-icon small left>mdi-plus</v-icon>
                  {{ $t("asaMeeting.addOwnTopic") }}
                </v-btn>
                <template v-if="canViewTickets">
                  <v-autocomplete
                    v-if="pickingTicket"
                    ref="ticketPicker"
                    v-model="pickedTicket"
                    :items="ticketOptions"
                    item-text="title"
                    item-value="_id"
                    return-object
                    :label="$t('asaMeeting.chooseTicket')"
                    dense
                    hide-details
                    autofocus
                    class="asa-ticket-picker"
                    @change="addTicketTopic"
                    @blur="pickingTicket = false"
                  />
                  <v-btn v-else text color="primary" small @click="startPickTicket">
                    <v-icon small left>mdi-plus</v-icon>
                    {{ $t("asaMeeting.addTicketTopic") }}
                  </v-btn>
                </template>
              </div>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="grey" outlined @click="goBack">{{ $t("general.back") }}</v-btn>
            <v-btn
              v-if="!isReadOnlyUser"
              color="primary"
              :loading="saving"
              :disabled="!form.date"
              @click="save"
            >
              {{ isNew ? $t("general.save") : $t("general.saveChanges") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { canView } from "../../utils/access";

function idList(list) {
  return (list || []).map((u) => String((u && u._id) || u));
}

function pickerDate(value) {
  return value ? String(value).slice(0, 10) : null;
}

function todayPickerDate() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export default {
  name: "asaMeetingForm",
  props: ["id", "mode"],
  data: () => ({
    form: { title: "", date: todayPickerDate(), topics: [] },
    participants: [],
    users: [],
    ticketOptions: [],
    pickingTicket: false,
    pickedTicket: null,
    dateMenu: false,
    saving: false,
    creatingTicket: null,
    currentUserId: null,
  }),
  computed: {
    isNew() {
      return this.mode === "new" || this.mode === "copy";
    },
    canViewTickets() {
      return canView("tickets");
    },
    priorityOptions() {
      return [
        { value: 1, text: this.$t("asaMeeting.priorities.low") },
        { value: 2, text: this.$t("asaMeeting.priorities.medium") },
        { value: 3, text: this.$t("asaMeeting.priorities.high") },
      ];
    },
  },
  async created() {
    const me = await this.$store.dispatch("getUserInfo");
    this.currentUserId = (me && me._id) || null;
    const users = await this.$store.dispatch("getUsersData");
    this.users = (users || [])
      .filter((u) => u && u.name)
      .map((u) => ({ _id: u._id, name: u.name }));

    if (this.mode !== "new") {
      const meeting = await this.$store.dispatch("getAsaMeeting", this.id);
      if (meeting) {
        this.form = {
          _id: meeting._id,
          title: meeting.title || "",
          date: meeting.date ? String(meeting.date).slice(0, 10) : todayPickerDate(),
          topics: (meeting.topics || []).map((t) => {
            const responsibles = idList(t.responsibles);
            const externalResponsibles = [...(t.externalResponsibles || [])];
            const dueDate = pickerDate(t.dueDate);
            const priority = t.priority || 1;
            const legacy = t.syncedPriority == null;
            return {
              title: t.title || "",
              comment: t.comment || "",
              responsibles,
              externalResponsibles,
              dueDate,
              priority,
              dueMenu: false,
              ticket: t.ticket || null,
              ticketSource: t.ticket ? t.ticketSource || "picked" : null,
              syncedTitle: t.ticket ? (t.syncedTitle ?? t.title ?? "") : "",
              syncedComment: t.ticket ? (t.syncedTitle ? t.syncedComment || "" : t.comment || "") : "",
              syncedResponsibles: t.ticket ? (legacy ? responsibles : idList(t.syncedResponsibles)) : [],
              syncedExternalResponsibles: t.ticket ? (legacy ? [...externalResponsibles] : [...(t.syncedExternalResponsibles || [])]) : [],
              syncedDueDate: t.ticket ? (legacy ? dueDate : pickerDate(t.syncedDueDate)) : null,
              syncedPriority: t.ticket ? (legacy ? priority : t.syncedPriority) : null,
            };
          }),
        };
        this.participants = (meeting.participants || []).map((p) => {
          if (!p.user) return p.name;
          return this.users.find((u) => u._id === String(p.user)) || { _id: String(p.user), name: p.name };
        });
        if (this.mode === "copy") this.prepareCopy();
      }
    }
  },
  methods: {
    formatDate(value) {
      if (!value) return "";
      const [y, m, d] = String(value).slice(0, 10).split("-");
      return `${d}.${m}.${y}`;
    },
    prepareCopy() {
      delete this.form._id;
      this.form.date = todayPickerDate();
      this.form.topics = this.form.topics.map((t) => ({
        ...t,
        comment: "",
        ticketSource: t.ticket ? "picked" : null,
        syncedTitle: t.ticket ? t.title : "",
        syncedComment: "",
        syncedResponsibles: t.ticket ? [...t.responsibles] : [],
        syncedExternalResponsibles: t.ticket ? [...t.externalResponsibles] : [],
        syncedDueDate: t.ticket ? t.dueDate : null,
        syncedPriority: t.ticket ? t.priority : null,
      }));
    },
    addOwnTopic() {
      this.form.topics.push({
        title: "",
        comment: "",
        responsibles: [],
        externalResponsibles: [],
        dueDate: null,
        priority: 1,
        dueMenu: false,
        ticket: null,
        ticketSource: null,
        syncedTitle: "",
        syncedComment: "",
        syncedResponsibles: [],
        syncedExternalResponsibles: [],
        syncedDueDate: null,
        syncedPriority: null,
      });
    },
    async startPickTicket() {
      if (!this.ticketOptions.length) {
        this.ticketOptions = await this.$store.dispatch("getAsaTicketOptions");
      }
      this.pickingTicket = true;
      await this.$nextTick();
      setTimeout(() => {
        const picker = this.$refs.ticketPicker;
        if (!picker) return;
        picker.focus();
        picker.activateMenu();
      });
    },
    addTicketTopic(ticket) {
      if (ticket) {
        const responsibles = idList(ticket.users);
        const externalResponsibles = [...(ticket.externalUsers || [])];
        const dueDate = pickerDate(ticket.dueDate);
        const priority = ticket.priority || 1;
        this.form.topics.push({
          title: ticket.title,
          comment: "",
          responsibles,
          externalResponsibles,
          dueDate,
          priority,
          dueMenu: false,
          ticket: ticket._id,
          ticketSource: "picked",
          syncedTitle: ticket.title,
          syncedComment: "",
          syncedResponsibles: [...responsibles],
          syncedExternalResponsibles: [...externalResponsibles],
          syncedDueDate: dueDate,
          syncedPriority: priority,
        });
      }
      this.pickedTicket = null;
      this.pickingTicket = false;
    },
    async createTicket(topic, idx) {
      this.creatingTicket = idx;
      try {
        const board = await this.$store.dispatch("getSettingData", "standardOpenBoard");
        const ticket = await this.$store.dispatch("addTicket", {
          recurring: 0,
          title: topic.title.trim(),
          description: topic.comment || "",
          dueDate: topic.dueDate ? new Date(topic.dueDate) : null,
          users: [],
          externalUsers: [...topic.externalResponsibles],
          priority: topic.priority || 1,
          board: board && board.value,
        });
        if (ticket && ticket._id) {
          for (const user of topic.responsibles) {
            await this.$store.dispatch("addUserToTicket", { user, id: ticket._id });
          }
          topic.ticket = ticket._id;
          topic.ticketSource = "created";
          topic.syncedTitle = topic.title.trim();
          topic.syncedComment = topic.comment || "";
          topic.syncedResponsibles = [...topic.responsibles];
          topic.syncedExternalResponsibles = [...topic.externalResponsibles];
          topic.syncedDueDate = topic.dueDate;
          topic.syncedPriority = topic.priority || 1;
          await this.persistAfterSync();
        }
      } finally {
        this.creatingTicket = null;
      }
    },
    needsSync(topic) {
      if (!topic.ticket) return false;
      return (
        topic.title.trim() !== (topic.syncedTitle || "") ||
        (topic.comment || "") !== (topic.syncedComment || "") ||
        this.responsiblesChanged(topic) ||
        (topic.dueDate || null) !== (topic.syncedDueDate || null) ||
        (topic.priority || 1) !== (topic.syncedPriority || 1)
      );
    },
    responsiblesChanged(topic) {
      const now = [...(topic.responsibles || [])].sort().join(",");
      const before = [...(topic.syncedResponsibles || [])].sort().join(",");
      return now !== before || this.externalResponsiblesChanged(topic);
    },
    externalResponsiblesChanged(topic) {
      const now = [...(topic.externalResponsibles || [])].sort().join("\n");
      const before = [...(topic.syncedExternalResponsibles || [])].sort().join("\n");
      return now !== before;
    },
    responsibleItems(topic) {
      const users = (topic.responsibles || []).map(
        (id) => this.users.find((u) => u._id === id) || { _id: id, name: id }
      );
      return [...users, ...(topic.externalResponsibles || [])];
    },
    setResponsibles(topic, list) {
      const ids = [];
      const names = [];
      (list || []).forEach((item) => {
        if (typeof item === "string") {
          const name = item.trim();
          const user = this.users.find((u) => u.name === name);
          if (user) {
            if (!ids.includes(user._id)) ids.push(user._id);
          } else if (name && !names.includes(name)) {
            names.push(name);
          }
        } else if (item && item._id && !ids.includes(item._id)) {
          ids.push(item._id);
        }
      });
      topic.responsibles = ids;
      topic.externalResponsibles = names;
    },
    async syncTicket(topic, idx) {
      this.creatingTicket = idx;
      try {
        const title = topic.title.trim();
        const comment = topic.comment || "";
        const commentChanged = comment !== (topic.syncedComment || "");
        const update = { _id: topic.ticket, title };
        if (topic.ticketSource === "created" && commentChanged) {
          update.description = comment;
        }
        if ((topic.dueDate || null) !== (topic.syncedDueDate || null)) {
          update.dueDate = topic.dueDate ? new Date(topic.dueDate) : null;
        }
        if ((topic.priority || 1) !== (topic.syncedPriority || 1)) {
          update.priority = topic.priority || 1;
        }
        const responsiblesChanged = this.responsiblesChanged(topic);
        const addedUsers = topic.responsibles.filter((u) => !topic.syncedResponsibles.includes(u));
        if (responsiblesChanged) {
          update.users = topic.responsibles.filter((u) => topic.syncedResponsibles.includes(u));
        }
        if (this.externalResponsiblesChanged(topic)) {
          update.externalUsers = [...topic.externalResponsibles];
        }
        const updated = await this.$store.dispatch("updateTicket", update);
        if (!updated) return;
        for (const user of addedUsers) {
          await this.$store.dispatch("addUserToTicket", { user, id: topic.ticket });
        }
        if (topic.ticketSource !== "created" && commentChanged && comment.trim()) {
          await this.$store.dispatch("addComment", {
            id: topic.ticket,
            content: `${this.$t("sidebar.asaMeetings")} ${this.formatDate(this.form.date)}: ${comment.trim()}`,
            user: this.currentUserId,
            timestamp: new Date(),
          });
        }
        topic.syncedTitle = title;
        topic.syncedComment = comment;
        topic.syncedResponsibles = [...topic.responsibles];
        topic.syncedExternalResponsibles = [...topic.externalResponsibles];
        topic.syncedDueDate = topic.dueDate;
        topic.syncedPriority = topic.priority || 1;
        await this.persistAfterSync();
      } finally {
        this.creatingTicket = null;
      }
    },
    async persistAfterSync() {
      if (this.isNew || !this.form._id) return;
      await this.$store.dispatch("updateAsaMeeting", { ...this.buildPayload(), _id: this.form._id });
    },
    buildPayload() {
      return {
        title: (this.form.title || "").trim(),
        date: this.form.date,
        participants: this.participants
          .map((p) =>
            typeof p === "string"
              ? { user: null, name: p.trim() }
              : { user: p._id || null, name: p.name }
          )
          .filter((p) => p.name),
        topics: this.form.topics
          .filter((t) => (t.title || "").trim())
          .map((t) => ({
            title: t.title.trim(),
            comment: t.comment || "",
            responsibles: t.responsibles || [],
            externalResponsibles: t.externalResponsibles || [],
            dueDate: t.dueDate || null,
            priority: t.priority || 1,
            ticket: t.ticket || null,
            ticketSource: t.ticket ? t.ticketSource || "picked" : null,
            syncedTitle: t.ticket ? t.syncedTitle || "" : "",
            syncedComment: t.ticket ? t.syncedComment || "" : "",
            syncedResponsibles: t.ticket ? t.syncedResponsibles || [] : [],
            syncedExternalResponsibles: t.ticket ? t.syncedExternalResponsibles || [] : [],
            syncedDueDate: t.ticket ? t.syncedDueDate || null : null,
            syncedPriority: t.ticket ? t.syncedPriority || 1 : null,
          })),
      };
    },
    async save() {
      if (!this.form.date) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        const saved = this.isNew
          ? await this.$store.dispatch("createAsaMeeting", payload)
          : await this.$store.dispatch("updateAsaMeeting", { ...payload, _id: this.form._id });
        if (!saved) return;
        const year = String(saved.date || payload.date).slice(0, 4);
        this.$router.push({ name: "asaMeetingList", params: { year } });
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      const year = this.$route.query.year;
      if (year) this.$router.push({ name: "asaMeetingList", params: { year } });
      else this.$router.push({ name: "asaMeetingYears" });
    },
  },
};
</script>

<style scoped>
.asa-topic__number {
  min-width: 26px;
  font-weight: 600;
}
.asa-topic__date ::v-deep .v-input__prepend-outer {
  margin-top: 2px;
  margin-bottom: 0;
}
.asa-ticket-picker {
  max-width: 420px;
}
</style>
